'use client';

/**
 * AffiliatePromoSuccessScreen
 *
 * Success screen shown after a completed affiliate promo purchase.
 * Replaces the standard PaymentProcessingScreen success state for affiliatePromo mode.
 *
 * Layout (matches old ProcessingStatus with isAffiliatePromoPopup=true):
 * - "Balance: $XX" — total new balance (purchaseAmount + bonus)
 * - Congratulations message with bonus amount highlighted in app color
 * - "If you need anything from us, contact us any time!" section
 * - OK button
 *
 * The app logo overlay (shown above the modal border) is rendered by the
 * parent via Popover's renderTopOverlay — not here.
 */

import React, { useState, useCallback } from 'react';
import { Trans } from 'react-i18next';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import { PURCHASE_FLOW_TEXT_KEYS } from '../utils/constants';
import { resolveTextKey } from '../../../../common/config/purchaseFlowRenderHelpers';
import { contactUsLink } from '../../../config/const';
import classes from './classes.module.scss';
import { SupportedCurrency } from '../types';
import { formatAmount } from '../utils/formatAmount';

// ============================================================================
// Types
// ============================================================================

export interface AffiliatePromoSuccessScreenProps {
  /** Total balance after purchase: purchaseAmount + bonus. Shown as "Balance: $XX". */
  totalBalance: number;
  /** Bonus credit amount. Passed to the congratulations Trans message. */
  bonus: number;
  /** Success button label key (from PURCHASE_FLOW_TEXT_KEYS). Defaults to "OK". */
  successButtonTextKey?: string;

  /** Currency to use for amount display (sign and ISO code) */
  currency: SupportedCurrency;

  onOkClick: () => void;
}

// ============================================================================
// Component
// ============================================================================

const AffiliatePromoSuccessScreen: React.FC<AffiliatePromoSuccessScreenProps> = ({
  totalBalance,
  bonus,
  successButtonTextKey,
  onOkClick,
  currency
}) => {
  const { renderText } = usePurchaseRender();
  const buttonTextKey = successButtonTextKey ?? PURCHASE_FLOW_TEXT_KEYS.BUTTON_OK;

  // Guard against double-clicks on the OK button
  const [clicked, setClicked] = useState(false);
  const handleOkClick = useCallback(() => {
    if (clicked) return;
    setClicked(true);
    onOkClick();
  }, [clicked, onOkClick]);

  return (
    <div className={ classes.affiliatePromoSuccessContainer } data-testid="affiliate-promo-success">
      <div className={ classes.affiliatePromoSuccessBalance } data-testid="affiliate-promo-success-balance">
        {renderText(PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_BALANCE, { amount: formatAmount(totalBalance, currency.sign) })}
      </div>

      <div className={ classes.affiliatePromoSuccessMessage } data-testid="affiliate-promo-success-message">
        <Trans
          i18nKey={ resolveTextKey(PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONGRATS) }
          components={ {
            normal: <p className={ classes.affiliatePromoSuccessNormalText } />,
            purpleText: <span className={ classes.affiliatePromoSuccessPurpleText } />
          } }
          values={ { amount: formatAmount(bonus, currency.sign) } }
        />
      </div>

      <button
        type="button"
        onClick={ handleOkClick }
        disabled={ clicked }
        className={ classes.processingActionButton }
        data-testid="affiliate-promo-success-ok-button"
      >
        {renderText(buttonTextKey)}
      </button>

      <div className={ classes.affiliatePromoSuccessContactUs } data-testid="affiliate-promo-success-contact">
        <span>
          {renderText(PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONTACT_PREFIX)}
          <a href={ contactUsLink.contactUs } className={ classes.affiliatePromoSuccessContactLink }>
            {renderText(PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONTACT_LINK_TEXT)}
          </a>
          {renderText(PURCHASE_FLOW_TEXT_KEYS.AFFILIATE_PROMO_SUCCESS_CONTACT_SUFFIX)}
        </span>
      </div>
    </div>
  );
};

export default AffiliatePromoSuccessScreen;
